/**
 * DOCX renderer.
 *
 * Converts a Word document to HTML entirely client-side with `mammoth`
 * (no server round-trip, no MS-Office dependency) and renders it read-only
 * inside a prose container. Fetches the raw bytes via the source's
 * `getBytes` (auth-correct for workspace routes), so the same renderer
 * serves the file browser, the Library, and chat attachments.
 *
 * The converted markup is model/agent-produced and rendered read-only, so
 * `dangerouslySetInnerHTML` is acceptable here — mammoth emits a constrained
 * subset (paragraphs, headings, lists, tables, links), no scripts.
 */
import mammoth from "mammoth";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import type { FileRendererProps } from "../types";

type DocxState =
  | { status: "idle" | "loading" }
  | { status: "ready"; html: string }
  | { status: "error"; error: Error };

export function DocxRenderer({ source }: FileRendererProps) {
  const [state, setState] = useState<DocxState>({ status: "idle" });

  useEffect(() => {
    if (!source.getBytes) {
      setState({
        status: "error",
        error: new Error("Source does not support byte access"),
      });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    source
      .getBytes()
      .then((buf) => mammoth.convertToHtml({ arrayBuffer: buf }))
      .then(({ value }) => {
        if (cancelled) return;
        setState({ status: "ready", html: value || "<p><em>(empty document)</em></p>" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
    return () => {
      cancelled = true;
    };
    // Keyed on source identity — same reasoning as useFileText/useFileBlobUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.id]);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Couldn&apos;t read this document.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background p-6">
      <div
        className="prose prose-sm mx-auto max-w-3xl dark:prose-invert"
        // mammoth emits a constrained, script-free subset (paragraphs,
        // headings, lists, tables, links); rendered read-only.
        dangerouslySetInnerHTML={{ __html: state.html }}
      />
    </div>
  );
}
